/**
 * ContactPaymentsSection — F-F.2
 *
 * Zeigt paginierte Zahlungen eines Contacts.
 * Graceful 404-Degradation: wird nicht gerendert wenn Endpoint nicht verfügbar.
 *
 * FIX #33: Spalten auf die echten BE-Felder umgestellt (time, memo statt
 * period_start/period_end/status/paid_at, die es beim Endpoint nicht gibt).
 */
import { useTranslation } from 'react-i18next'
import { useContactPayments } from './useContactPayments'
import { activeBcp47 } from '../../lib/locale'

type Props = {
  contactId: string
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(activeBcp47(), { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function SkeletonRows() {
  return (
    <>
      {[1, 2, 3].map((i) => (
        <tr key={i} aria-hidden="true">
          <td><div className="skeleton-line" style={{ width: 90, height: 13 }} /></td>
          <td><div className="skeleton-line" style={{ width: 60, height: 13 }} /></td>
          <td><div className="skeleton-line" style={{ width: 40, height: 13 }} /></td>
          <td><div className="skeleton-line" style={{ width: 120, height: 13 }} /></td>
        </tr>
      ))}
    </>
  )
}

export function ContactPaymentsSection({ contactId }: Props) {
  const { t } = useTranslation('contacts')
  const { payments, isLoading, isError, isUnavailable, hasMore, loadMore } = useContactPayments(contactId)

  if (isUnavailable) return null

  return (
    <div className="card card-pad" style={{ marginTop: 24 }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>{t('payments.title')}</h2>

      {isError && (
        <div className="error-state" role="alert" style={{ fontSize: 14 }}>
          {t('payments.errorLoad')}
        </div>
      )}

      {!isError && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '6px 12px 6px 0', fontWeight: 600 }}>{t('payments.columns.date')}</th>
                <th style={{ padding: '6px 12px 6px 0', fontWeight: 600 }}>{t('payments.columns.amount')}</th>
                <th style={{ padding: '6px 12px 6px 0', fontWeight: 600 }}>{t('payments.columns.currency')}</th>
                <th style={{ padding: '6px 0 6px 0', fontWeight: 600 }}>{t('payments.columns.memo')}</th>
              </tr>
            </thead>
            <tbody data-testid="contact-payments-list">
              {isLoading && <SkeletonRows />}
              {!isLoading && payments.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 0', color: 'var(--text-subtle)', fontSize: 14 }}>
                    {t('payments.empty')}
                  </td>
                </tr>
              )}
              {payments.map((payment) => (
                <tr
                  key={payment.id}
                  data-testid={`payment-row-${payment.id}`}
                  style={{ borderBottom: '1px solid var(--border-subtle, var(--border))' }}
                >
                  <td style={{ padding: '8px 12px 8px 0' }}>
                    {formatDate(payment.time)}
                  </td>
                  <td style={{ padding: '8px 12px 8px 0', fontVariantNumeric: 'tabular-nums' }}>
                    {payment.amount.toFixed(2)}
                  </td>
                  <td style={{ padding: '8px 12px 8px 0' }}>
                    {payment.currency.toUpperCase()}
                  </td>
                  <td style={{ padding: '8px 0' }}>
                    {payment.memo ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && !isLoading && (
        <div style={{ marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={loadMore}
            data-testid="load-more-payments"
          >
            {t('payments.loadMore')}
          </button>
        </div>
      )}
    </div>
  )
}
